import { Injectable } from '@angular/core';

/**
 * Interface for cache entry with metadata
 */
interface ICacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number | null;
}

/**
 * Cache configuration options
 */
export interface ICacheOptions {
  /** Time to live in milliseconds. Null means no expiration */
  ttl?: number | null;
  /** Use localStorage for persistence across sessions. Default is sessionStorage */
  persistent?: boolean;
}

/**
 * Generic cache service following Single Responsibility Principle
 * Handles data caching with optional TTL and persistence strategies
 *
 * @example
 * // Cache with 5 minute TTL
 * cacheService.set('projects', data, { ttl: 5 * 60 * 1000 });
 *
 * // Get cached data
 * const cached = cacheService.get<IFullProject[]>('projects');
 */
@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private readonly memoryCache = new Map<string, ICacheEntry<any>>();
  private readonly CACHE_PREFIX = 'techstack_cache_';

  constructor() {}

  /**
   * Store data in cache with optional TTL
   */
  set<T>(key: string, data: T, options: ICacheOptions = {}): void {
    const { ttl = null, persistent = false } = options;

    const entry: ICacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: ttl ? Date.now() + ttl : null,
    };

    if (persistent) {
      this._setInStorage(key, entry, localStorage);
    } else {
      this._setInStorage(key, entry, sessionStorage);
    }

    // Always keep in memory for faster access
    this.memoryCache.set(key, entry);
  }

  /**
   * Retrieve data from cache. Returns null if not found or expired
   */
  get<T>(key: string): T | null {
    // Try memory first (fastest)
    let entry: ICacheEntry<T> | null =
      (this.memoryCache.get(key) as ICacheEntry<T> | undefined) ?? null;

    // If not in memory, try storage
    if (!entry) {
      entry = this._getFromStorage<T>(key, sessionStorage);
      if (!entry) {
        entry = this._getFromStorage<T>(key, localStorage);
      }

      // Restore to memory if found
      if (entry) {
        this.memoryCache.set(key, entry);
      }
    }

    if (!entry) {
      return null;
    }

    // Check expiration
    if (entry.expiresAt && Date.now() > entry.expiresAt) {
      this.remove(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Check if cache entry exists and is valid
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Get cache age in milliseconds
   */
  getAge(key: string): number | null {
    const entry =
      this.memoryCache.get(key) ||
      this._getFromStorage(key, sessionStorage) ||
      this._getFromStorage(key, localStorage);

    if (!entry) {
      return null;
    }

    return Date.now() - entry.timestamp;
  }

  /**
   * Remove data from cache
   */
  remove(key: string): void {
    this.memoryCache.delete(key);
    sessionStorage.removeItem(this._getStorageKey(key));
    localStorage.removeItem(this._getStorageKey(key));
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.memoryCache.clear();
    this._clearStorageByPrefix(sessionStorage);
    this._clearStorageByPrefix(localStorage);
  }

  /**
   * Get or fetch pattern: Return cached data immediately, optionally refresh in background
   */
  async getOrFetch<T>(
    key: string,
    fetchFn: () => Promise<T>,
    options: ICacheOptions & { refreshInBackground?: boolean } = {},
  ): Promise<{ data: T; fromCache: boolean }> {
    const cached = this.get<T>(key);
    const { refreshInBackground = false } = options;

    if (cached) {
      // Return cached data immediately
      if (refreshInBackground) {
        // Refresh in background without waiting
        fetchFn()
          .then((freshData) => {
            this.set(key, freshData, options);
          })
          .catch((error) => {
            console.error(`Background refresh failed for ${key}:`, error);
          });
      }

      return { data: cached, fromCache: true };
    }

    // No cache, fetch fresh data
    const freshData = await fetchFn();
    this.set(key, freshData, options);
    return { data: freshData, fromCache: false };
  }

  /**
   * Private helper to store in web storage
   */
  private _setInStorage<T>(
    key: string,
    entry: ICacheEntry<T>,
    storage: Storage,
  ): void {
    try {
      storage.setItem(this._getStorageKey(key), JSON.stringify(entry));
    } catch (error) {
      console.warn(`Failed to cache ${key} in storage:`, error);

      // Si es QuotaExceededError, limpiar caché viejo
      if ((error as any).name === 'QuotaExceededError') {
        this._clearStorageByPrefix(storage);
        // Reintentar
        try {
          storage.setItem(this._getStorageKey(key), JSON.stringify(entry));
        } catch (retryError) {
          console.error('Cache storage failed even after cleanup:', retryError);
        }
      }
    }
  }

  /**
   * Private helper to retrieve from web storage
   */
  private _getFromStorage<T>(
    key: string,
    storage: Storage,
  ): ICacheEntry<T> | null {
    try {
      const item = storage.getItem(this._getStorageKey(key));
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.warn(`Failed to retrieve ${key} from storage:`, error);
      // Eliminar entrada corrupta
      storage.removeItem(this._getStorageKey(key));
      return null;
    }
  }

  /**
   * Get prefixed storage key
   */
  private _getStorageKey(key: string): string {
    return `${this.CACHE_PREFIX}${key}`;
  }

  /**
   * Clear all items with cache prefix from storage
   */
  private _clearStorageByPrefix(storage: Storage): void {
    const keysToRemove: string[] = [];

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(this.CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => storage.removeItem(key));
  }
}
